import { vars } from "./contract.css";
import { focusRingState } from "./mixins.css";

import type { StyleRule } from "@vanilla-extract/css";

import { emphasize, focusRing, tint } from "./utils";

export const FIELD_VARIANTS = ["default", "filled", "ghost"] as const;

export type FieldVariant = (typeof FIELD_VARIANTS)[number];

const variantStyles = (focus: string): Record<FieldVariant, StyleRule> => ({
  default: {
    backgroundColor: vars.color.background,
    borderColor: vars.color.border,
    selectors: { [focus]: focusRingState },
  },
  filled: {
    backgroundColor: vars.color.surfaceHover,
    borderColor: vars.color.border,
    selectors: {
      [focus]: {
        borderColor: emphasize(vars.color.primaryHover, 10),
        boxShadow: focusRing(vars.color.primary),
      },
    },
  },
  ghost: {
    backgroundColor: "transparent",
    borderColor: "transparent",
    selectors: {
      [focus]: { borderColor: "transparent", boxShadow: "none" },
    },
  },
});

export const fieldVariantStyles = <V extends FieldVariant>(
  focus: string,
  variants: readonly V[],
): Record<V, StyleRule> => {
  const all = variantStyles(focus);

  return Object.fromEntries(variants.map((v) => [v, all[v]])) as Record<
    V,
    StyleRule
  >;
};

export const fieldStatusFocus = (color: string): StyleRule => ({
  borderColor: emphasize(color, 30),
  boxShadow: focusRing(color, 40),
});

export const fieldStatusStyle = (color: string, focus: string): StyleRule => ({
  borderColor: color,
  selectors: { [focus]: fieldStatusFocus(color) },
});

export const fieldStatusStyles = (focus: string) => ({
  invalid: { true: fieldStatusStyle(vars.color.error, focus), false: {} },
  success: { true: fieldStatusStyle(vars.color.success, focus), false: {} },
});

export const fieldFilledStatusVariants = {
  invalid: {
    variants: { variant: "filled", invalid: true },
    style: { backgroundColor: tint(vars.color.error, 12) },
  },
  success: {
    variants: { variant: "filled", success: true },
    style: { backgroundColor: tint(vars.color.success, 12) },
  },
} as const;
